import { useRef } from "react";
import { DraftFunction } from "use-immer";
import { NutriSearchFilter, TextSearch } from "../data/search";
import { MealSourceDto } from "../api";

export function usePerformSearch<T>(
  activePerson: number | undefined,
  nutriFilter: NutriSearchFilter,
  searchTerm: string,
  searchResults: T[],
  updateSearchResults: (arg: DraftFunction<T[]> | T[]) => void,
  setSearchResultCount: (
    value: ((prevState: number) => number) | number,
  ) => void,
  search: (options: { body: TextSearch }) => Promise<{
    data: { count: number; searchResults: T[] };
  }>,
  pageSize?: number,
  indicators?: MealSourceDto["indicator"][],
  tags?: string[],
) {
  // Token, um veraltete Antworten (z. B. noch laufende Nachlade-Requests nach
  // einer neuen Suche) zu verwerfen.
  const requestIdRef = useRef(0);
  // Parameter der letzten Suche, damit Nachladen immer zur angezeigten Suche
  // passt (auch wenn der Nutzer schon einen neuen Begriff tippt).
  const lastSearchRef = useRef<TextSearch | null>(null);

  const buildSearchObject = (fromIndex: number, term: string): TextSearch => ({
    nutriSearchFilter: {
      ...nutriFilter,
      activePerson,
    },
    searchText: term,
    pageSize,
    fromIndex,
    indicators: indicators && indicators.length > 0 ? indicators : undefined,
    tags: tags && tags.length > 0 ? tags : undefined,
  });

  const performSearch = async (isNewSearch: boolean, termOverride?: string) => {
    const term = termOverride ?? searchTerm;
    if (isNewSearch) {
      // Neue Suche: laufende Anfragen invalidieren und vorherige Treffer
      // entfernen, damit nichts Altes in der Liste stehen bleibt.
      requestIdRef.current += 1;
      updateSearchResults([]);
      setSearchResultCount(0);
    }
    const requestId = requestIdRef.current;
    let searchObject: TextSearch;
    if (isNewSearch) {
      searchObject = buildSearchObject(0, term);
      lastSearchRef.current = searchObject;
    } else if (lastSearchRef.current) {
      searchObject = {
        ...lastSearchRef.current,
        fromIndex: searchResults.length,
      };
    } else {
      searchObject = buildSearchObject(searchResults.length, term);
    }

    const response = await search({ body: searchObject });
    // Antwort verwerfen, wenn zwischenzeitlich eine neue Suche gestartet wurde.
    if (requestId !== requestIdRef.current) return;
    if (isNewSearch) {
      updateSearchResults(response.data.searchResults);
    } else {
      updateSearchResults((searchResultsDraft) => {
        searchResultsDraft.push(...(response.data.searchResults as any));
      });
    }
    setSearchResultCount(response.data.count);
  };

  const fetchMoreData = () => {
    performSearch(false);
  };

  return { performSearch, fetchMoreData };
}
